import React, { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Snackbar,
  TextField,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  adminSurfaceSx,
  adminPageSx,
  adminChipSx,
  MONO,
} from "./adminStyles";
import AdminPageHeader from "./AdminPageHeader";
import AdminSectionHeader from "./AdminSectionHeader";
import { adminRequest } from "../../utils/adminApi";

dayjs.extend(relativeTime);

// Admin "Coach Context" console (worker #476): enter a user's email and see
// everything the coach has stored about them — the data that feeds its system
// prompt and tool calls. Fires through the getCoachContext proxy → worker
// GET /api/admin/coach-context?user=<email> (admin-gated server-side).
//
// Read-only by design. Every recent coach defect (#468 stale chips citing erased
// facts, #419 deload on a clean session, #449 fabricated tool failure) was a
// CONTEXT problem that was not inspectable without a manual Durable Object dig.
// This tab is that dig, rendered.
//
// env-aware: prod uses the authSlice token; staging uses the held staging token
// and routes via ?env=staging (adminRequest), exactly like Coach Chats.

// Reflection is "stale" when the coach has been told its memory is dirty but has
// not re-reflected since. `dirty` alone is normal and transient — it clears on
// the next reflection pass. Dirty for longer than this threshold means the pass
// is not running, which is the state that makes memory look wrong. The worker
// does not classify this (it returns the raw flag + timestamp), so the threshold
// lives here, is named, and is stated on screen rather than being an invisible
// client-side opinion.
export const REFLECTION_STALE_AFTER_HOURS = 24;

// Timestamps come off the DO as SQLite's CURRENT_TIMESTAMP
// ("YYYY-MM-DD HH:MM:SS", UTC, no zone marker). dayjs would read that as LOCAL
// time and shift it — normalise to ISO-UTC first so the displayed time is right.
const normalizeTs = (raw) =>
  /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)
    ? raw.replace(" ", "T") + "Z"
    : raw;

const fmtTs = (raw) => {
  if (!raw) return "—";
  const d = dayjs(normalizeTs(raw));
  return d.isValid() ? d.format("MMM D, YYYY HH:mm") : raw;
};

const fmtRelative = (raw) => {
  if (!raw) return "never";
  const d = dayjs(normalizeTs(raw));
  return d.isValid() ? d.fromNow() : raw;
};

const hoursSince = (raw) => {
  if (!raw) return null;
  const d = dayjs(normalizeTs(raw));
  return d.isValid() ? dayjs().diff(d, "hour", true) : null;
};

// age_seconds → "6h 12m" / "45m" / "30s". The suggestions cache is the surface
// that caused a real prod bug (6h-stale chips citing facts the user had erased),
// so its age is spelled out, not left as a raw integer.
const fmtAge = (seconds) => {
  if (seconds === null || seconds === undefined) return "—";
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

// Group facts by category, newest first WITHIN each category, and order the
// categories by their newest fact so the coach's most recent learning is at the
// top of the page — the debugging question is almost always "what did it just
// learn / un-learn?", not "what does the alphabet say".
const groupFacts = (facts) => {
  const byCategory = new Map();
  for (const f of facts) {
    const key = f.category || "uncategorised";
    if (!byCategory.has(key)) byCategory.set(key, []);
    byCategory.get(key).push(f);
  }
  const ts = (f) => {
    const d = dayjs(normalizeTs(f.created_at));
    return d.isValid() ? d.valueOf() : 0;
  };
  const groups = [...byCategory.entries()].map(([category, items]) => {
    const sorted = [...items].sort((a, b) => ts(b) - ts(a));
    return {
      category,
      facts: sorted,
      newest: sorted.length ? ts(sorted[0]) : 0,
      activeCount: sorted.filter((f) => f.status !== "superseded").length,
      supersededCount: sorted.filter((f) => f.status === "superseded").length,
    };
  });
  return groups.sort((a, b) => b.newest - a.newest);
};

// The profile blob is deliberately OPAQUE in the contract — it is whatever the
// worker stitches together from UserInformation2 / WorkoutStyles / Goals, and it
// will grow. Rendering it generically means a new profile field shows up here
// the day the worker adds it, with no web change.
const renderProfileValue = (value, theme) => {
  if (value === null || value === undefined || value === "") {
    return (
      <Typography
        variant="body2"
        sx={{ color: theme.palette.text.secondary, fontStyle: "italic" }}
      >
        —
      </Typography>
    );
  }
  if (typeof value === "object") {
    return (
      <Box
        component="pre"
        sx={{
          m: 0,
          fontFamily: MONO,
          fontSize: "0.75rem",
          color: theme.palette.text.primary,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {JSON.stringify(value, null, 2)}
      </Box>
    );
  }
  return (
    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
      {String(value)}
    </Typography>
  );
};

export default function CoachContext() {
  const theme = useTheme();
  const { env, token } = useSelector(adminRequest);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState(null); // null = never searched
  const [searchedEmail, setSearchedEmail] = useState("");
  const [snack, setSnack] = useState("");

  const load = useCallback(async () => {
    const target = email.trim();
    if (!target) {
      setEmailError("User email is required");
      return;
    }
    setEmailError("");
    if (env === "staging" && !token) {
      setSnack("Log in to staging first.");
      return;
    }
    setLoading(true);
    try {
      // env is routing-only; the proxy strips it before forwarding.
      const params = new URLSearchParams({ user: target });
      if (env === "staging") params.set("env", "staging");
      const response = await axios.get(
        `/api/admin/getCoachContext?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setContext(response.data ?? null);
      setSearchedEmail(target);
    } catch (err) {
      console.error("Failed to load coach context:", err);
      const message =
        err.response?.data?.error ||
        err.message ||
        "Failed to load coach context.";
      setSnack(message);
      // Do NOT fall back to a synthetic `exists: false` — a failed request and
      // "the coach has no record of this user" are different answers, and
      // conflating them is exactly the confusion this tab exists to remove.
      setContext(null);
      setSearchedEmail(target);
    } finally {
      setLoading(false);
    }
  }, [email, env, token]);

  const onKeyDown = (e) => {
    if (e.key === "Enter") load();
  };

  const copyPrompt = useCallback(async () => {
    const text = context?.rendered_prompt_block || "";
    try {
      await navigator.clipboard.writeText(text);
      setSnack("Rendered prompt block copied.");
    } catch (err) {
      console.error("Clipboard write failed:", err);
      setSnack("Could not copy — select the text and copy manually.");
    }
  }, [context]);

  const factGroups = useMemo(
    () => groupFacts(context?.memory?.facts ?? []),
    [context]
  );

  const exists = context?.exists === true;
  const reflection = context?.reflection;
  const reflectionHours = hoursSince(reflection?.last_reflected_at);
  // Dirty AND (never reflected OR not reflected within the window) — the state
  // that explains "why does memory look wrong".
  const reflectionStuck =
    !!reflection?.dirty &&
    (reflectionHours === null || reflectionHours > REFLECTION_STALE_AFTER_HOURS);

  const cache = context?.suggestions_cache;
  const cacheStale = cache?.stale === true;

  return (
    <Box>
      <Box sx={adminPageSx}>
        <AdminPageHeader
          icon={<PsychologyIcon />}
          iconAccent={theme.palette.primary.main}
          title="Coach context"
          subtitle="Everything the coach has stored about a user — memory facts, narrative, profile, reflection freshness, cached chips, and the exact system-prompt block the model sees. Read-only."
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8, lg: 6 }}>
            <Card sx={adminSurfaceSx(theme)}>
              <CardContent>
                <TextField
                  fullWidth
                  label="User email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={onKeyDown}
                  error={!!emailError}
                  helperText={emailError}
                  margin="normal"
                  slotProps={{
                    htmlInput: { "data-testid": "coach-context-email" },
                  }}
                />
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  sx={{ mt: 1 }}
                >
                  <Button
                    variant="contained"
                    startIcon={
                      loading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SearchIcon />
                      )
                    }
                    onClick={load}
                    disabled={loading}
                    data-testid="coach-context-load"
                  >
                    Load context
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* exists:false — the coach has NO record. This must never be mistaken
            for "the user exists but has learned nothing"; that distinction is
            the whole reason the contract carries an `exists` flag. */}
        {context && !exists && (
          <Card
            sx={{ ...adminSurfaceSx(theme), mt: 3 }}
            data-testid="coach-context-absent"
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ color: theme.palette.warning.main, mt: 0.25 }}>
                  <PersonOffIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                  >
                    No coach record for {searchedEmail}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
                  >
                    The coach has never stored anything under this address — no
                    Durable Object was found. This is <strong>not</strong> the
                    same as a user whose memory is empty: that user would render
                    below with zero facts. Check the address for a typo, or
                    confirm the account exists on this environment
                    {env === "staging" ? " (staging)" : " (prod)"}.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {context && exists && (
          <Box sx={{ mt: 3 }}>
            {/* ---- Freshness banners: why memory may look wrong ------------ */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card
                  sx={adminSurfaceSx(theme, {
                    accent: reflectionStuck
                      ? theme.palette.error.main
                      : undefined,
                    active: reflectionStuck,
                  })}
                  data-testid="coach-context-reflection"
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <Box
                        sx={{
                          color: reflectionStuck
                            ? theme.palette.error.main
                            : theme.palette.success.main,
                          display: "flex",
                        }}
                      >
                        {reflectionStuck ? (
                          <WarningAmberIcon fontSize="small" />
                        ) : (
                          <CheckCircleOutlineIcon fontSize="small" />
                        )}
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: theme.palette.text.primary,
                        }}
                      >
                        Reflection
                      </Typography>
                      <Chip
                        size="small"
                        label={reflection?.dirty ? "DIRTY" : "clean"}
                        sx={adminChipSx(
                          theme,
                          reflection?.dirty
                            ? theme.palette.warning.main
                            : theme.palette.success.main
                        )}
                        data-testid="coach-context-reflection-dirty"
                      />
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary }}
                    >
                      Last reflected{" "}
                      <strong>
                        {fmtRelative(reflection?.last_reflected_at)}
                      </strong>
                      {reflection?.last_reflected_at
                        ? ` (${fmtTs(reflection.last_reflected_at)})`
                        : ""}
                    </Typography>
                    {reflectionStuck && (
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.error.main, mt: 1 }}
                        data-testid="coach-context-reflection-stuck"
                      >
                        Memory is marked dirty but has not been re-reflected in
                        over {REFLECTION_STALE_AFTER_HOURS}h. The coach is
                        reading stale memory — this alone can explain wrong
                        recall.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card
                  sx={adminSurfaceSx(theme, {
                    accent: cacheStale ? theme.palette.error.main : undefined,
                    active: cacheStale,
                  })}
                  data-testid="coach-context-cache"
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <Box
                        sx={{
                          color: cacheStale
                            ? theme.palette.error.main
                            : theme.palette.success.main,
                          display: "flex",
                        }}
                      >
                        {cacheStale ? (
                          <WarningAmberIcon fontSize="small" />
                        ) : (
                          <CheckCircleOutlineIcon fontSize="small" />
                        )}
                      </Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: theme.palette.text.primary,
                        }}
                      >
                        Suggestions cache
                      </Typography>
                      {cacheStale && (
                        <Chip
                          size="small"
                          label="STALE"
                          sx={adminChipSx(theme, theme.palette.error.main)}
                          data-testid="coach-context-cache-stale"
                        />
                      )}
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary }}
                    >
                      Age <strong>{fmtAge(cache?.age_seconds)}</strong>
                      {cache?.updated_at
                        ? ` · written ${fmtTs(cache.updated_at)}`
                        : " · never written"}
                    </Typography>
                    {cacheStale && (
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.error.main, mt: 1 }}
                      >
                        These chips are past their TTL and may cite facts the
                        user has since erased (the #468 defect). They will be
                        served until regenerated.
                      </Typography>
                    )}
                    {cache?.payload !== null &&
                      cache?.payload !== undefined && (
                        <Box
                          component="pre"
                          sx={{
                            mt: 1.5,
                            mb: 0,
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: alpha("#000", 0.35),
                            border: `1px solid ${alpha("#ffffff", 0.08)}`,
                            fontFamily: MONO,
                            fontSize: "0.72rem",
                            color: theme.palette.text.primary,
                            maxHeight: 160,
                            overflow: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {JSON.stringify(cache.payload, null, 2)}
                        </Box>
                      )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* ---- The headline: what the model actually sees -------------- */}
            <Box sx={{ mb: 3 }}>
              <AdminSectionHeader
                label="Rendered prompt block"
                caption="The exact untrusted user_profile_and_memory block injected into the coach's system prompt. This is the highest-fidelity answer to “what goes into the tool calls”."
                action={
                  <Tooltip title="Copy to clipboard">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={copyPrompt}
                        disabled={!context?.rendered_prompt_block}
                        data-testid="coach-context-copy-prompt"
                      >
                        Copy
                      </Button>
                    </span>
                  </Tooltip>
                }
              />
              <Box
                component="pre"
                data-testid="coach-context-prompt-block"
                sx={{
                  m: 0,
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: alpha("#000", 0.35),
                  border: `1px solid ${alpha("#ffffff", 0.08)}`,
                  fontFamily: MONO,
                  fontSize: "0.78rem",
                  lineHeight: 1.6,
                  color: theme.palette.text.primary,
                  maxHeight: 420,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {context.rendered_prompt_block || "(empty)"}
              </Box>
            </Box>

            {/* ---- Memory facts ------------------------------------------- */}
            <Box sx={{ mb: 3 }}>
              <AdminSectionHeader
                label="Memory facts"
                caption={`Grouped by category, newest first. Superseded facts are the coach's un-learn audit trail — kept visible, struck through.`}
              />
              {factGroups.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                  data-testid="coach-context-no-facts"
                >
                  This user exists, but the coach has stored no memory facts yet.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {factGroups.map((group) => (
                    <Card
                      key={group.category}
                      sx={adminSurfaceSx(theme)}
                      data-testid="coach-context-fact-group"
                    >
                      <CardContent>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 1.5, flexWrap: "wrap" }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.text.primary,
                            }}
                          >
                            {group.category}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${group.activeCount} active`}
                            sx={adminChipSx(theme, theme.palette.success.main)}
                          />
                          {group.supersededCount > 0 && (
                            <Chip
                              size="small"
                              label={`${group.supersededCount} superseded`}
                              sx={adminChipSx(
                                theme,
                                theme.palette.text.secondary
                              )}
                            />
                          )}
                        </Stack>
                        <Stack
                          divider={<Divider flexItem />}
                          spacing={1.25}
                        >
                          {group.facts.map((fact) => {
                            const superseded = fact.status === "superseded";
                            return (
                              <Box
                                key={fact.id}
                                data-testid={
                                  superseded
                                    ? "coach-context-fact-superseded"
                                    : "coach-context-fact"
                                }
                                sx={{ pt: 0.25, opacity: superseded ? 0.55 : 1 }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: theme.palette.text.primary,
                                    textDecoration: superseded
                                      ? "line-through"
                                      : "none",
                                  }}
                                >
                                  {fact.text}
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  sx={{ mt: 0.5, flexWrap: "wrap" }}
                                >
                                  {superseded && (
                                    <Chip
                                      size="small"
                                      label="superseded"
                                      sx={adminChipSx(
                                        theme,
                                        theme.palette.text.secondary
                                      )}
                                    />
                                  )}
                                  {/* `source` separates what the agent chose to
                                      write from what the system derived — the
                                      first question when a fact looks wrong. */}
                                  <Chip
                                    size="small"
                                    label={`source: ${fact.source ?? "unknown"}`}
                                    sx={adminChipSx(
                                      theme,
                                      theme.palette.primary.light
                                    )}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: theme.palette.text.secondary,
                                      fontFamily: MONO,
                                    }}
                                  >
                                    {fmtTs(fact.created_at)} ·{" "}
                                    {fmtRelative(fact.created_at)}
                                  </Typography>
                                </Stack>
                              </Box>
                            );
                          })}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>

            {/* ---- Narrative ---------------------------------------------- */}
            <Box sx={{ mb: 3 }}>
              <AdminSectionHeader
                label="Narrative"
                caption={
                  context.memory?.narrative?.updated_at
                    ? `Updated ${fmtTs(
                        context.memory.narrative.updated_at
                      )} (${fmtRelative(context.memory.narrative.updated_at)})`
                    : "Never written."
                }
              />
              <Card sx={adminSurfaceSx(theme)}>
                <CardContent>
                  {context.memory?.narrative?.text ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.7,
                      }}
                      data-testid="coach-context-narrative"
                    >
                      {context.memory.narrative.text}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontStyle: "italic",
                      }}
                      data-testid="coach-context-narrative-empty"
                    >
                      No narrative stored for this user.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* ---- Profile (opaque — rendered generically) ----------------- */}
            <Box sx={{ mb: 3 }}>
              <AdminSectionHeader
                label="Profile"
                caption="Profile, preferences, goals and injuries as the coach reads them. Rendered generically from whatever the worker returns, so new fields appear here automatically."
              />
              <Card sx={adminSurfaceSx(theme)}>
                <CardContent>
                  {context.profile &&
                  typeof context.profile === "object" &&
                  Object.keys(context.profile).length > 0 ? (
                    <Grid container spacing={2} data-testid="coach-context-profile">
                      {Object.entries(context.profile).map(([key, value]) => (
                        <Grid size={{ xs: 12, sm: 6 }} key={key}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              textTransform: "uppercase",
                              letterSpacing: "0.06em",
                              fontWeight: 700,
                              display: "block",
                              mb: 0.25,
                            }}
                          >
                            {key}
                          </Typography>
                          {renderProfileValue(value, theme)}
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontStyle: "italic",
                      }}
                      data-testid="coach-context-profile-empty"
                    >
                      No profile stored for this user.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack("")}
        message={snack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
